import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SidebarCollapsibleSection } from 'components/blocks/layout/sidebar-section';
import { IconName } from 'components/blocks/menu-icon/menu-icon';
import { useTranslation } from 'react-i18next';
import { CreateAssistantModal } from './create-assistant-modal';
import { useListCustomGpts, useDeleteCustomGpt } from '../hooks/use-custom-gpt';
import { SidebarRichItem } from 'components/blocks/layout/sidebar-items';
import { ASSISTANT_ICONS } from '../constants/assistant-icons';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { Icon } from 'components/blocks/menu-icon/menu-icon';
import { useToast } from 'hooks/use-toast';
import { useCustomGptStore } from 'state/store/chat/use-custom-gpt-store';
import { listFilesForCustomGpt } from '../services/custom-gpt-service';
import { useConfig } from 'hooks/use-config';
import { useQueryClient } from '@tanstack/react-query';
import { CustomGptFile } from '../types/custom-gpt.types';


export const MyAssistantsSection = () => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { data: assistants, isLoading } = useListCustomGpts();
    const { mutate: deleteAssistant } = useDeleteCustomGpt();
    const { toast } = useToast();
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();

    const getAssistantIconSrc = (logoId?: string) => {
        if (!logoId) return undefined;
        const icon = ASSISTANT_ICONS.find(i => i.id === logoId);
        return icon?.src;
    };

    const handleDeleteAssistant = (id: string, name: string) => {
        deleteAssistant(id, {
            onSuccess: () => {
                toast({
                    title: t('SUCCESS') || 'Success',
                    description: t('ASSISTANT_DELETED_SUCCESSFULLY', { name }) || `Assistant "${name}" deleted successfully.`,
                });
            },
            onError: (error) => {
                console.error('Failed to delete assistant:', error);
                toast({
                    title: t('ERROR') || 'Error',
                    description: t('FAILED_TO_DELETE_ASSISTANT') || 'Failed to delete assistant.',
                    variant: 'destructive',
                });
            }
        });
    };

    const { setInitialData } = useCustomGptStore();

    const handleEditAssistant = async (id: string) => {
        const assistant = assistants?.data?.find(a => a.id === id);
        if (assistant) {
            let files: CustomGptFile[] = [];
            try {
                if (configurationSettings) {
                    files = await queryClient.fetchQuery({
                        queryKey: ['custom-gpt-files', id],
                        queryFn: () => listFilesForCustomGpt(configurationSettings, id)
                    });
                }
            } catch (error) {
                console.error('Failed to fetch assistant files:', error);
            }

            setInitialData({
                id: assistant.id,
                name: assistant.name,
                description: assistant.description,
                systemPrompt: assistant.systemPrompt,
                modelName: assistant.modelName,
                temperature: assistant.temperature,
                logoUrl: assistant.logoUrl,
                files: files
            });
            setIsCreateModalOpen(true);
        }
    };

    const renderRightAction = (id: string, name: string) => (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    className="p-1 rounded-md hover:bg-muted-foreground/10"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Icon
                        name="Ellipsis"
                        size={18}
                        strokeWidth={2.2}
                        className="text-sidebar-foreground group-hover:text-primary"
                    />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end" className="min-w-40 rounded-[8px]">
                <DropdownMenuItem onClick={() => handleEditAssistant(id)}>
                    <Icon name="Pencil" size={16} className="mr-2" />
                    {t('EDIT') || 'Edit'}
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => handleDeleteAssistant(id, name)}
                    className="text-red-500 focus:text-red-500"
                >
                    <Icon name="Trash2" size={16} className="mr-2" />
                    {t('DELETE') || 'Delete'}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );

    return (
        <div className="mt-4 mb-6">
            <SidebarCollapsibleSection
                title={t("MY_ASSISTANTS") || "My Assistants"}
                icon={'Bot' as IconName}
                onAddClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setIsCreateModalOpen(true);
                }}
            >
                <div className="flex flex-col gap-1">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                        </div>
                    ) : assistants && assistants.data && assistants.data.length > 0 ? (
                        assistants.data.map((assistant) => (
                            <SidebarRichItem
                                key={assistant.id}
                                title={assistant.name}
                                subtitle={assistant.visibility ? assistant.visibility.charAt(0).toUpperCase() + assistant.visibility.slice(1) : ''}
                                image={getAssistantIconSrc(assistant.logoUrl)}
                                rightAction={renderRightAction(assistant.id, assistant.name)}
                                onClick={() => {
                                    navigate(`/custom-gpts/${assistant.id}`);
                                }}
                            />
                        ))
                    ) : (
                        <div className="px-3 py-2">
                            <p className="text-xs text-muted-foreground italic">
                                {t("NO_ASSISTANTS_FOUND") || "No assistants found"}
                            </p>
                        </div>
                    )}
                </div>
            </SidebarCollapsibleSection>

            <CreateAssistantModal
                isOpen={isCreateModalOpen}
                setIsOpen={setIsCreateModalOpen}
            />
        </div>
    );
};
